import { useState, useCallback, useEffect } from "react";
import { z } from "zod";
import {
  cloudProviderSchemas,
  type CloudProviderSchemaTypes,
} from "@pineseed/spec/cloud-provider-schemas";
import type { UseAdminAppReturn } from "./useAdminApp";

// API shapes — inferred from the canonical spec
export type CloudProviderTool = CloudProviderSchemaTypes["CloudProviderTool"];
export type CloudProviderHook = CloudProviderSchemaTypes["CloudProviderHook"];
export type CloudProviderHitlHandler = CloudProviderSchemaTypes["CloudProviderHitlHandler"];
export type CloudProviderTenantTool = CloudProviderSchemaTypes["CloudProviderTenantTool"];
export type CloudProviderTenantHook = CloudProviderSchemaTypes["CloudProviderTenantHook"];
export type CloudProviderTenantHitlHandler = CloudProviderSchemaTypes["CloudProviderTenantHitlHandler"];
export type CreateCloudProviderHookRequest = CloudProviderSchemaTypes["CreateCloudProviderHookRequest"];
export type UpdateCloudProviderHookRequest = CloudProviderSchemaTypes["UpdateCloudProviderHookRequest"];

export interface CloudProviderAdminHandler {
  // Selection state
  selectedAccountId: string;
  setSelectedAccountId: (id: string) => void;

  tools: CloudProviderTool[];
  hooks: CloudProviderHook[];
  hitlHandlers: CloudProviderHitlHandler[];
  tenantTools: CloudProviderTenantTool[];
  tenantHooks: CloudProviderTenantHook[];
  tenantHitlHandlers: CloudProviderTenantHitlHandler[];
  loading: boolean;
  error: string | null;

  loadTools: () => Promise<void>;
  loadHooks: () => Promise<void>;
  loadHitlHandlers: () => Promise<void>;
  loadTenantTools: () => Promise<void>;
  loadTenantHooks: () => Promise<void>;
  loadTenantHitlHandlers: () => Promise<void>;
  loadAll: () => Promise<void>;

  upsertTool: (data: Record<string, unknown>) => Promise<void>;
  updateTool: (handlerName: string, data: Record<string, unknown>) => Promise<void>;
  deleteTool: (handlerName: string) => Promise<void>;

  upsertHook: (data: CreateCloudProviderHookRequest) => Promise<void>;
  updateHook: (name: string, data: UpdateCloudProviderHookRequest) => Promise<void>;
  deleteHook: (name: string) => Promise<void>;

  upsertHitlHandler: (data: Record<string, unknown>) => Promise<void>;
  updateHitlHandler: (name: string, data: Record<string, unknown>) => Promise<void>;
  deleteHitlHandler: (name: string) => Promise<void>;

  upsertTenantTool: (data: Record<string, unknown>) => Promise<void>;
  updateTenantTool: (handlerName: string, data: Record<string, unknown>) => Promise<void>;
  deleteTenantTool: (handlerName: string) => Promise<void>;

  upsertTenantHook: (data: CreateCloudProviderHookRequest) => Promise<void>;
  updateTenantHook: (name: string, data: UpdateCloudProviderHookRequest) => Promise<void>;
  deleteTenantHook: (name: string) => Promise<void>;

  upsertTenantHitlHandler: (data: Record<string, unknown>) => Promise<void>;
  updateTenantHitlHandler: (name: string, data: Record<string, unknown>) => Promise<void>;
  deleteTenantHitlHandler: (name: string) => Promise<void>;
}

export function useCloudProviderAdmin(adminApp: UseAdminAppReturn, accounts: { entities: Array<{ id: string; name: string }> }): CloudProviderAdminHandler {
  const [tools, setTools] = useState<CloudProviderTool[]>([]);
  const [hooks, setHooks] = useState<CloudProviderHook[]>([]);
  const [hitlHandlers, setHitlHandlers] = useState<CloudProviderHitlHandler[]>([]);
  const [tenantTools, setTenantTools] = useState<CloudProviderTenantTool[]>([]);
  const [tenantHooks, setTenantHooks] = useState<CloudProviderTenantHook[]>([]);
  const [tenantHitlHandlers, setTenantHitlHandlers] = useState<CloudProviderTenantHitlHandler[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper: build base URL and fetch with credentials
  const baseFetch = useCallback(async (path: string, options?: RequestInit) => {
    const url = `${adminApp.httpUrl}/api/cloud-provider/${path}`;
    const res = await fetch(url, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Workspace-Id": adminApp.workspaceId,
        ...(options?.headers || {}),
      },
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`${res.status}: ${body}`);
    }
    // 204 No Content (delete)
    if (res.status === 204) return null;
    return res.json();
  }, [adminApp.httpUrl, adminApp.workspaceId]);

  // Account selection — driven by account dropdown, not resolved from hierarchy
  const [selectedAccountId, setSelectedAccountId] = useState<string>(() => {
    return accounts.entities[0]?.id ?? "";
  });

  // Update selection when accounts load
  useEffect(() => {
    const [firstAccount] = accounts.entities;
    if (!selectedAccountId && firstAccount) {
      setSelectedAccountId(firstAccount.id);
    }
  }, [accounts.entities]);

  const requireAccountId = useCallback((): string => {
    if (!selectedAccountId) throw new Error("No account selected");
    return selectedAccountId;
  }, [selectedAccountId]);

  // Load operations
  const loadTools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await baseFetch(`workspace/${adminApp.workspaceId}/tools`);
      setTools(z.array(cloudProviderSchemas.CloudProviderTool).parse(data));
    } catch (err) {
      setError(`Failed to load tools: ${err instanceof Error ? err.message : String(err)}`);
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, adminApp.workspaceId]);

  const loadHooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await baseFetch(`workspace/${adminApp.workspaceId}/hooks`);
      setHooks(z.array(cloudProviderSchemas.CloudProviderHook).parse(data));
    } catch (err) {
      setError(`Failed to load hooks: ${err instanceof Error ? err.message : String(err)}`);
      setHooks([]);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, adminApp.workspaceId]);

  const loadHitlHandlers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await baseFetch(`workspace/${adminApp.workspaceId}/hitl-handlers`);
      setHitlHandlers(z.array(cloudProviderSchemas.CloudProviderHitlHandler).parse(data));
    } catch (err) {
      setError(`Failed to load HITL handlers: ${err instanceof Error ? err.message : String(err)}`);
      setHitlHandlers([]);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, adminApp.workspaceId]);

  // Load operations — Tenant (use selectedAccountId directly)
  const loadTenantTools = useCallback(async () => {
    if (!selectedAccountId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await baseFetch(`tenant/${selectedAccountId}/tools`);
      setTenantTools(z.array(cloudProviderSchemas.CloudProviderTenantTool).parse(data));
    } catch (err) {
      setError(`Failed to load tenant tools: ${err instanceof Error ? err.message : String(err)}`);
      setTenantTools([]);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, selectedAccountId]);

  const loadTenantHooks = useCallback(async () => {
    if (!selectedAccountId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await baseFetch(`tenant/${selectedAccountId}/hooks`);
      setTenantHooks(z.array(cloudProviderSchemas.CloudProviderTenantHook).parse(data));
    } catch (err) {
      setError(`Failed to load tenant hooks: ${err instanceof Error ? err.message : String(err)}`);
      setTenantHooks([]);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, selectedAccountId]);

  const loadTenantHitlHandlers = useCallback(async () => {
    if (!selectedAccountId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await baseFetch(`tenant/${selectedAccountId}/hitl-handlers`);
      setTenantHitlHandlers(z.array(cloudProviderSchemas.CloudProviderTenantHitlHandler).parse(data));
    } catch (err) {
      setError(`Failed to load tenant HITL handlers: ${err instanceof Error ? err.message : String(err)}`);
      setTenantHitlHandlers([]);
    } finally {
      setLoading(false);
    }
  }, [baseFetch, selectedAccountId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadTools(), loadHooks(), loadHitlHandlers(),
        loadTenantTools(), loadTenantHooks(), loadTenantHitlHandlers(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [loadTools, loadHooks, loadHitlHandlers, loadTenantTools, loadTenantHooks, loadTenantHitlHandlers]);

  // Auto-load on workspace change
  useEffect(() => {
    loadTools(); loadHooks(); loadHitlHandlers();
  }, [adminApp.workspaceId]);

  // Auto-load tenant data on account selection change
  useEffect(() => {
    if (selectedAccountId) {
      loadTenantTools(); loadTenantHooks(); loadTenantHitlHandlers();
    }
  }, [selectedAccountId]);

  // Mutation operations — Tools
  const upsertTool = useCallback(async (data: Record<string, unknown>) => {
    const aid = requireAccountId();
    await baseFetch(`workspace/${aid}/${adminApp.workspaceId}/tool`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    await loadTools();
  }, [baseFetch, adminApp.workspaceId, requireAccountId, loadTools]);

  const updateTool = useCallback(async (handlerName: string, data: Record<string, unknown>) => {
    const aid = requireAccountId();
    await baseFetch(`workspace/${aid}/${adminApp.workspaceId}/tool/${encodeURIComponent(handlerName)}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    await loadTools();
  }, [baseFetch, adminApp.workspaceId, requireAccountId, loadTools]);

  const deleteTool = useCallback(async (handlerName: string) => {
    const aid = requireAccountId();
    await baseFetch(`workspace/${aid}/${adminApp.workspaceId}/tool/${encodeURIComponent(handlerName)}`, {
      method: "DELETE",
    });
    await loadTools();
  }, [baseFetch, adminApp.workspaceId, requireAccountId, loadTools]);

  // Mutation operations — Hooks
  const upsertHook = useCallback(async (data: CreateCloudProviderHookRequest) => {
    const aid = requireAccountId();
    await baseFetch(`workspace/${aid}/${adminApp.workspaceId}/hook`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    await loadHooks();
  }, [baseFetch, adminApp.workspaceId, requireAccountId, loadHooks]);

  const updateHook = useCallback(async (name: string, data: UpdateCloudProviderHookRequest) => {
    const aid = requireAccountId();
    await baseFetch(`workspace/${aid}/${adminApp.workspaceId}/hook/${encodeURIComponent(name)}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    await loadHooks();
  }, [baseFetch, adminApp.workspaceId, requireAccountId, loadHooks]);

  const deleteHook = useCallback(async (name: string) => {
    const aid = requireAccountId();
    await baseFetch(`workspace/${aid}/${adminApp.workspaceId}/hook/${encodeURIComponent(name)}`, {
      method: "DELETE",
    });
    await loadHooks();
  }, [baseFetch, adminApp.workspaceId, requireAccountId, loadHooks]);

  // Mutation operations — HITL Handlers
  const upsertHitlHandler = useCallback(async (data: Record<string, unknown>) => {
    const aid = requireAccountId();
    await baseFetch(`workspace/${aid}/${adminApp.workspaceId}/hitl-handler`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    await loadHitlHandlers();
  }, [baseFetch, adminApp.workspaceId, requireAccountId, loadHitlHandlers]);

  const updateHitlHandler = useCallback(async (name: string, data: Record<string, unknown>) => {
    const aid = requireAccountId();
    await baseFetch(`workspace/${aid}/${adminApp.workspaceId}/hitl-handler/${encodeURIComponent(name)}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    await loadHitlHandlers();
  }, [baseFetch, adminApp.workspaceId, requireAccountId, loadHitlHandlers]);

  const deleteHitlHandler = useCallback(async (name: string) => {
    const aid = requireAccountId();
    await baseFetch(`workspace/${aid}/${adminApp.workspaceId}/hitl-handler/${encodeURIComponent(name)}`, {
      method: "DELETE",
    });
    await loadHitlHandlers();
  }, [baseFetch, adminApp.workspaceId, requireAccountId, loadHitlHandlers]);

  // Mutation operations — Tenant Tools
  const upsertTenantTool = useCallback(async (data: Record<string, unknown>) => {
    const aid = requireAccountId();
    await baseFetch(`tenant/${aid}/tool`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    await loadTenantTools();
  }, [baseFetch, requireAccountId, loadTenantTools]);

  const updateTenantTool = useCallback(async (handlerName: string, data: Record<string, unknown>) => {
    const aid = requireAccountId();
    await baseFetch(`tenant/${aid}/tool/${encodeURIComponent(handlerName)}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    await loadTenantTools();
  }, [baseFetch, requireAccountId, loadTenantTools]);

  const deleteTenantTool = useCallback(async (handlerName: string) => {
    const aid = requireAccountId();
    await baseFetch(`tenant/${aid}/tool/${encodeURIComponent(handlerName)}`, {
      method: "DELETE",
    });
    await loadTenantTools();
  }, [baseFetch, requireAccountId, loadTenantTools]);

  // Mutation operations — Tenant Hooks
  const upsertTenantHook = useCallback(async (data: CreateCloudProviderHookRequest) => {
    const aid = requireAccountId();
    await baseFetch(`tenant/${aid}/hook`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    await loadTenantHooks();
  }, [baseFetch, requireAccountId, loadTenantHooks]);

  const updateTenantHook = useCallback(async (name: string, data: UpdateCloudProviderHookRequest) => {
    const aid = requireAccountId();
    await baseFetch(`tenant/${aid}/hook/${encodeURIComponent(name)}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    await loadTenantHooks();
  }, [baseFetch, requireAccountId, loadTenantHooks]);

  const deleteTenantHook = useCallback(async (name: string) => {
    const aid = requireAccountId();
    await baseFetch(`tenant/${aid}/hook/${encodeURIComponent(name)}`, {
      method: "DELETE",
    });
    await loadTenantHooks();
  }, [baseFetch, requireAccountId, loadTenantHooks]);

  // Mutation operations — Tenant HITL Handlers
  const upsertTenantHitlHandler = useCallback(async (data: Record<string, unknown>) => {
    const aid = requireAccountId();
    await baseFetch(`tenant/${aid}/hitl-handler`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    await loadTenantHitlHandlers();
  }, [baseFetch, requireAccountId, loadTenantHitlHandlers]);

  const updateTenantHitlHandler = useCallback(async (name: string, data: Record<string, unknown>) => {
    const aid = requireAccountId();
    await baseFetch(`tenant/${aid}/hitl-handler/${encodeURIComponent(name)}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    await loadTenantHitlHandlers();
  }, [baseFetch, requireAccountId, loadTenantHitlHandlers]);

  const deleteTenantHitlHandler = useCallback(async (name: string) => {
    const aid = requireAccountId();
    await baseFetch(`tenant/${aid}/hitl-handler/${encodeURIComponent(name)}`, {
      method: "DELETE",
    });
    await loadTenantHitlHandlers();
  }, [baseFetch, requireAccountId, loadTenantHitlHandlers]);

  return {
    selectedAccountId, setSelectedAccountId,
    tools, hooks, hitlHandlers,
    tenantTools, tenantHooks, tenantHitlHandlers,
    loading, error,
    loadTools, loadHooks, loadHitlHandlers,
    loadTenantTools, loadTenantHooks, loadTenantHitlHandlers,
    loadAll,
    upsertTool, updateTool, deleteTool,
    upsertHook, updateHook, deleteHook,
    upsertHitlHandler, updateHitlHandler, deleteHitlHandler,
    upsertTenantTool, updateTenantTool, deleteTenantTool,
    upsertTenantHook, updateTenantHook, deleteTenantHook,
    upsertTenantHitlHandler, updateTenantHitlHandler, deleteTenantHitlHandler,
  };
}
